export interface SuggestedPricePageLoadInput {
  requestedPage: number;
  requestedPageSize: number;
  urlPage: number;
  urlPageSize: number;
  filterString: string;
  previousFilterString: string | null;
  hasLoaded: boolean;
  appliedPageSize: number | null;
  requestId: number;
  latestRequestId: number;
  responseCount: number | null;
  responseRowCount: number;
  previousTotalCount: number | null;
}

export interface SuggestedPricePageLoadDecision {
  page: number;
  pageSize: number;
  resetToPageZero: boolean;
  apply: boolean;
  totalCount: number;
  pagerPage: number;
}

const positive = (value: number | null | undefined): value is number =>
  typeof value === 'number' && !Number.isNaN(value) && value > 0;

/**
 * Page-load decision shared by both Suggested Price by Margin lists.
 * A newer page or page-size change may start while a load is in flight.
 * The pager total is the response count (every matching row), never the
 * number of rows on the current page.
 */
export function resolveSuggestedPricePageLoad(
  input: SuggestedPricePageLoadInput,
): SuggestedPricePageLoadDecision {
  const resetToPageZero =
    input.previousFilterString !== null &&
    input.previousFilterString !== input.filterString;

  const pageSizeChanged =
    positive(input.appliedPageSize) &&
    input.requestedPageSize !== input.appliedPageSize;

  let pageSize = input.requestedPageSize;
  if (!input.hasLoaded && positive(input.urlPageSize)) {
    pageSize = input.urlPageSize;
  }
  if (pageSizeChanged) {
    pageSize = input.requestedPageSize;
  }
  if (!positive(pageSize)) {
    pageSize = positive(input.urlPageSize) ? input.urlPageSize : 10;
  }

  let page = input.requestedPage;
  if (resetToPageZero || pageSizeChanged) {
    page = 0;
  } else if (!input.hasLoaded && input.requestedPage === 0 && positive(input.urlPage)) {
    page = input.urlPage;
  }
  if (!Number.isFinite(page) || page < 0) {
    page = 0;
  }

  const apply = input.requestId === input.latestRequestId;

  const hasFullCount =
    typeof input.responseCount === 'number' && !Number.isNaN(input.responseCount);

  let totalCount = 0;
  if (hasFullCount) {
    totalCount = input.responseCount as number;
  } else if (positive(input.previousTotalCount)) {
    totalCount = input.previousTotalCount;
  }

  const pagerPage = hasFullCount && totalCount === 0 ? 0 : page;

  return {
    page,
    pageSize,
    resetToPageZero,
    apply,
    totalCount,
    pagerPage,
  };
}
